import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Select, Table, Tooltip } from 'antd';
import { Users, UserCheck, Home, Calendar, Search, Upload, Download, RefreshCw, ArrowUpRight, Shield, Filter, CheckCircle, AlertCircle } from 'react-feather';
import { countries } from 'countries-list';
import axios from 'axios';
import GuestImportModal from '../../components/popups/GuestImportModal';
import GuestProfileModal from '../../components/popups/GuestProfileModal';
import './guestCrm.css';
import { CONTACT_SOURCES, getContactSourceLabel } from '../../utils/guestContactSources';

const apiURL = process.env.REACT_APP_API_URL || 'https://api.example.com';
const EMPTY_METRICS = null;
const PAGE_SIZE = 10;
const dateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const segments = [
  { value: '', label: 'All guests' },
  { value: 'Returning', label: 'Returning' },
  { value: 'FirstStay', label: 'First stay' },
  { value: 'InHouse', label: 'In-house' },
  { value: 'Upcoming', label: 'Upcoming' },
  { value: 'NeverStayed', label: 'No stays yet' },
  { value: 'Regular', label: 'Marked regular' },
];
const getInitials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';
const getRelationship = (guest) => {
  if (guest.in_house) return { label: 'In-house', css: 'inhouse' };
  if (Number(guest.completed_stays) >= 2) return { label: 'Returning', css: 'returning' };
  if (Number(guest.completed_stays) === 1) return { label: 'First stay', css: 'firststay' };
  return { label: 'No stays yet', css: 'new' };
};

const GuestDatabase = () => {
  const [guests, setGuests] = useState([]);
  const [listError, setListError] = useState('');
  const [metricsError, setMetricsError] = useState('');
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('');
  const [nationality, setNationality] = useState([]);
  const [contactSource, setContactSource] = useState(undefined);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [historicalStays, setHistoricalStays] = useState([]);
  const [importVisible, setImportVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const listRequestRef = useRef(0);
  const profileRequestRef = useRef(0);
  const tokenHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
  const nationalityOptions = useMemo(() => Object.values(countries)
    .map(c => ({ label: c.name, value: c.name })).sort((a, b) => a.label.localeCompare(b.label)), []);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchText.trim()); setPagination(p => ({ ...p, current: 1 })); }, 350);
    return () => clearTimeout(timer);
  }, [searchText]);

  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);
  useEffect(() => {
    let active = true;
    setMetricsError('');
    axios.get(`${apiURL}/reports-daily/guest-metrics/`, { headers: tokenHeaders() })
      .then(({ data }) => { if (active) {
        if (data.success && data.metrics) setMetrics(data.metrics);
        else { setMetrics(null); setMetricsError('Guest insights are unavailable.'); }
      } })
      .catch(() => { if (active) { setMetrics(null); setMetricsError('Guest insights could not be loaded.'); } });
    return () => { active = false; };
  }, [refreshKey]);

  useEffect(() => {
    const requestId = ++listRequestRef.current;
    setLoading(true);
    setListError('');
    axios.get(`${apiURL}/reports-daily/guest-list/`, {
      headers: tokenHeaders(),
      params: {
        page: pagination.current, pageSize: pagination.pageSize,
        search: search || undefined, guestType: segment || undefined,
        nationality: nationality.length ? nationality.join(',') : undefined,
        contactSource: contactSource || undefined,
      },
    }).then(({ data }) => {
      if (requestId !== listRequestRef.current) return;
      if (!data.success || !Array.isArray(data.guests)) throw new Error('Invalid guest API response.');
      setGuests(data.guests);
      setPagination(p => ({ ...p, total: Number(data.pagination?.totalRecords) || 0 }));
    }).catch(() => {
      if (requestId !== listRequestRef.current) return;
      setGuests([]);
      setListError('We could not load guests. Please retry; no sample or placeholder guests are shown.');
    }).finally(() => { if (requestId === listRequestRef.current) setLoading(false); });
  }, [pagination.current, pagination.pageSize, segment, nationality, contactSource, search, refreshKey]);

  const openProfile = (guest) => {
    const requestId = ++profileRequestRef.current;
    setSelectedGuest(guest);
    setHistoricalStays([]);
    setProfileError('');
    setProfileLoading(true);
    axios.get(`${apiURL}/reports-daily/guest-bookings/${guest.guest_id}`, { headers: tokenHeaders() })
      .then(({ data }) => {
        if (requestId !== profileRequestRef.current) return;
        if (data.success && Array.isArray(data.bookings)) setHistoricalStays(data.bookings);
        else setProfileError('Stay history is unavailable.');
      }).catch(() => {
        if (requestId === profileRequestRef.current) setProfileError('Unable to load this guest’s stay history.');
      }).finally(() => {
        if (requestId === profileRequestRef.current) setProfileLoading(false);
      });
  };
  const closeProfile = () => { ++profileRequestRef.current; setSelectedGuest(null); setHistoricalStays([]); };
  const exportHeaders = [
    { key: 'guest_id', label: 'Guest ID' }, { key: 'full_name', label: 'Name' },
    { key: 'phone_no', label: 'Phone' }, { key: 'email', label: 'Email' },
    { key: 'city', label: 'City' }, { key: 'nationality', label: 'Nationality' },
    { key: 'contact_source_code', label: 'Contact Source', render: getContactSourceLabel },
    { key: 'completed_stays', label: 'Completed Stays' },
    { key: 'last_stay', label: 'Last Checkout', render: dateLabel },
  ];
  const exportPage = () => {
    if (!guests.length) return;
    // Only the currently displayed, company-scoped page is exported. Protect against
    // spreadsheet formula injection and escape quotes/newlines correctly.
    const csvCell = value => {
      let text = value == null ? '' : String(value);
      if (/^[\s]*[=+\-@]/.test(text)) text = `\'${text}`;
      return `"${text.replace(/"/g, '""')}"`;
    };
    const rows = guests.map(row => exportHeaders.map(h => {
      const value = h.render ? h.render(row[h.key]) : row[h.key];
      return csvCell(value);
    }).join(','));
    const csv = [exportHeaders.map(h => csvCell(h.label)).join(','), ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `guest_crm_page_${pagination.current}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const cards = [
    { label: 'Guest profiles', value: metrics?.totalGuests, note: 'All guest profiles in this property', icon: Users, tone: 'blue' },
    { label: 'Returning guests', value: metrics?.returningGuests, note: 'Two or more completed stays', icon: UserCheck, tone: 'violet' },
    { label: 'In-house guests', value: metrics?.inHouseGuests, note: 'Currently checked in', icon: Home, tone: 'teal' },
    { label: 'Recent visitors', value: metrics?.recentStays, note: 'Checked out in the last 30 days', icon: Calendar, tone: 'amber' },
  ];
  const columns = [
    {
      title: 'GUEST', dataIndex: 'full_name', key: 'name', width: 240,
      render: (value, guest) => <button type="button" className="gcrm-guest-button" onClick={() => openProfile(guest)}>
        <span className="gcrm-avatar">{getInitials(value)}</span>
        <span className="gcrm-guest-identity"><strong title={value || 'Unnamed guest'}>{value || 'Unnamed guest'}</strong><small>Guest #{guest.guest_id}{guest.city ? ` · ${guest.city}` : ''}</small></span>
      </button>,
    },
    {
      title: 'CONTACT', key: 'contact', width: 245, render: (_, guest) => <div className="gcrm-cell-stack">
        <span className={guest.phone_no ? 'gcrm-primary-contact' : 'gcrm-muted'}>{guest.phone_no || 'No phone recorded'}</span>
        <small className={guest.email ? '' : 'gcrm-contact-missing'}>{guest.email || 'No email recorded'}</small>
      </div>,
    },
    { title: 'RELATIONSHIP', key: 'segment', width: 170, render: (_, guest) => {
      const rel = getRelationship(guest);
      return <div className="gcrm-cell-stack"><span className={`gcrm-pill ${rel.css}`}><span className="gcrm-pill-dot" />{rel.label}</span>
        {guest.is_regular && <small className="gcrm-regular-note">Marked regular</small>}</div>;
    } },
    { title: 'CONTACT SOURCE', key: 'contact_source', width: 175, render: (_, guest) =>
      <span className={`gcrm-source-tag${guest.contact_source_code ? '' : ' is-empty'}`}>{getContactSourceLabel(guest.contact_source_code)}</span> },
    { title: 'STAYS', key: 'completed_stays', width: 90, render: (_, guest) => <span className="gcrm-number">{Number(guest.completed_stays) || 0}</span> },
    { title: 'LAST CHECKOUT', dataIndex: 'last_stay', key: 'last_stay', width: 148, render: value => <span className="gcrm-date">{dateLabel(value)}</span> },
    { title: 'NEXT VISIT', key: 'upcoming', width: 120, render: (_, guest) => guest.upcoming ? <span className="gcrm-pill upcoming"><span className="gcrm-pill-dot" />Reserved</span> : <span className="gcrm-muted">—</span> },
    { title: '', key: 'action', width: 56, render: (_, guest) => <Tooltip title="Open guest profile"><Button className="gcrm-row-action" type="text" aria-label={`Open profile for ${guest.full_name || 'guest'}`} onClick={() => openProfile(guest)} icon={<ArrowUpRight size={15} />} /></Tooltip> },
  ];
  return <div className="page-wrapper"><div className="content container-fluid"><main className="gcrm">
    <header className="gcrm-header">
      <div className="gcrm-header-copy">
        <div className="gcrm-eyebrow"><Shield size={13} /> NETSHINE HOSPITALITY <span className="gcrm-eyebrow-dot" /> GUEST RELATIONSHIPS</div>
        <h1>Guest CRM</h1>
        <p>A clearer view of every guest, their stays, and the next interaction.</p>
      </div>
      <div className="gcrm-header-actions">
        <Button icon={<RefreshCw size={15} />} onClick={refresh} aria-label="Refresh guest data">Refresh</Button>
        <Button icon={<Download size={15} />} disabled={!guests.length} onClick={exportPage} title="Exports the current page only, without identity documents">Export page</Button>
        <Button type="primary" icon={<Upload size={15} />} onClick={() => setImportVisible(true)}>Import guests</Button>
      </div>
    </header>
    <section className="gcrm-overview" aria-label="Guest CRM overview">
      <div className="gcrm-metrics">
        {cards.map(({ label, value, note, icon: Icon, tone }) => <div className="gcrm-metric" key={label} title={note}>
          <span className={`gcrm-metric-icon ${tone}`} aria-hidden="true"><Icon size={18} /></span>
          <div className="gcrm-metric-content"><span className="gcrm-metric-label">{label}</span>
            <strong>{value === undefined || value === null ? '—' : Number(value).toLocaleString('en-IN')}</strong>
            <small>{note}</small>
          </div>
        </div>)}
      </div>
      {metricsError && <Alert className="gcrm-error" type="warning" showIcon message={metricsError} />}
      <div className="gcrm-insights" aria-label="Guest directory indicators">
        <div className="gcrm-insight"><CheckCircle size={15} aria-hidden="true" /><strong>{metrics?.regularGuests ?? '—'}</strong><span>marked regular</span></div>
        <div className="gcrm-insight"><Calendar size={15} aria-hidden="true" /><strong>{metrics?.upcomingGuests ?? '—'}</strong><span>with future reservations</span></div>
        <div className="gcrm-insight"><AlertCircle size={15} aria-hidden="true" /><strong>{metrics?.incompleteProfiles ?? '—'}</strong><span>missing phone or email</span></div>
      </div>
    </section>
    <section className="gcrm-directory" aria-labelledby="gcrm-directory-title">
      <div className="gcrm-directory-top">
        <div className="gcrm-directory-heading">
          <div className="gcrm-directory-title-row"><h2 id="gcrm-directory-title">Guest directory</h2>
            <span className="gcrm-count">{pagination.total.toLocaleString('en-IN')} {pagination.total === 1 ? 'profile' : 'profiles'}</span>
          </div>
          <p>Search and manage guest relationships across their stay history.</p>
        </div>
        <div className="gcrm-toolbar">
          <Input className="gcrm-search" size="large" prefix={<Search size={16} color="#71829b" />} value={searchText}
            onChange={e => setSearchText(e.target.value)} allowClear placeholder="Search name, phone or email" aria-label="Search guests" />
          <Button className={showFilters ? 'gcrm-filter-active' : ''} icon={<Filter size={15} />} onClick={() => setShowFilters(v => !v)} aria-expanded={showFilters}>
            Filters{nationality.length + (contactSource ? 1 : 0) ? ` (${nationality.length + (contactSource ? 1 : 0)})` : ''}</Button>
        </div>
      </div>
      <nav className="gcrm-segments" aria-label="Guest segments">{segments.map(s => <button type="button" aria-current={segment === s.value ? 'page' : undefined}
        className={segment === s.value ? 'active' : ''} key={s.value}
        onClick={() => { setSegment(s.value); setPagination(p => ({ ...p, current: 1 })); }}>{s.label}</button>)}</nav>
      {showFilters && <div className="gcrm-advanced"><div><label>Nationality</label><Select mode="multiple" allowClear showSearch
        style={{ width: '100%', minWidth: 220 }} placeholder="Any nationality" value={nationality}
        options={nationalityOptions} onChange={value => { setNationality(value); setPagination(p => ({ ...p, current: 1 })); }} /></div>
        <div><label>Contact source</label><Select allowClear showSearch style={{ width: 235 }} value={contactSource}
          placeholder="All contact sources" options={[...CONTACT_SOURCES, { value: 'unassigned', label: 'Not specified' }]}
          onChange={value => { setContactSource(value); setPagination(p => ({ ...p, current: 1 })); }} /></div>
        <Button onClick={() => { setNationality([]); setContactSource(undefined); setSegment(''); setSearchText(''); setSearch(''); setPagination(p => ({ ...p, current: 1 })); }}>Clear filters</Button></div>}
      {listError && <Alert className="gcrm-error" type="error" showIcon message={listError} action={<Button size="small" onClick={refresh}>Retry</Button>} />}
      <Table className="gcrm-table" rowKey="guest_id" dataSource={guests} columns={columns} loading={loading}
        scroll={{ x: 1220 }} locale={{ emptyText: listError ? 'Guest records unavailable' : 'No guests match these filters.' }}
        pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
          showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          onChange: (page, pageSize) => setPagination(p => ({ ...p, current: pageSize !== p.pageSize ? 1 : page, pageSize })) }} />
      <div className="gcrm-table-footer"><Shield size={13} aria-hidden="true" /><span>Guests are scoped to your company. Stay counts exclude cancelled bookings. Export includes only this page.</span></div>
    </section>
    <GuestProfileModal visible={!!selectedGuest} onClose={closeProfile} guest={selectedGuest} historicalStays={historicalStays}
      loading={profileLoading} error={profileError} />
    <GuestImportModal visible={importVisible} onClose={() => setImportVisible(false)} onImportSuccess={refresh} />
  </main></div></div>;
};

export default GuestDatabase;
